import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  CommissioningDeviceAssignChannelParams,
  CommissioningStore,
  DeviceAssignmentType,
  DevicesStore,
  InstallationFeaturesStore,
} from '@store';
import { zip } from 'rxjs';
import { delay, filter, map, retryWhen, take } from 'rxjs/operators';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

export enum AssignChannelDisplayState {
  Loading,
  AssignRooms,
  ErrorNoRooms,
  ErrorNoChannels,
  Error,
}

@Component({
  selector: 'app-commissioning-assign-channel-modal',
  templateUrl: './commissioning-assign-channel-modal.component.html',
  styleUrls: ['./commissioning-assign-channel-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAssignChannelModalComponent implements OnInit {
  AssignChannelDisplayState = AssignChannelDisplayState;
  channelsOptions: {
    id: string;
    value: string;
    viewValue: string;
    title: string;
  }[] = [];

  roomsOptions: {
    value: string;
    viewValue: string;
  }[] = [];

  devicesLoading: boolean;

  constructor(
    public commissioningService: CommissioningService,
    private commissioningStore: CommissioningStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService,
    private devicesStore: DevicesStore,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningAssignChannelModalComponent>
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');

    this.devicesLoading = true;

    zip(
      this.commissioningStore.pairedDeviceFhtChannels(),
      this.installationFeaturesStore.roomsCompatibileWithFhtChannels$
    )
      .pipe(
        map(([fhtChannels, _rooms]) => {
          if (fhtChannels.length === 0) {
            this.devicesStore.updateDevices();
            throw new Error();
          }

          return [fhtChannels, _rooms];
        }),
        retryWhen((errors) => {
          return errors.pipe(delay(5000));
        }),
        filter(([fhtChannels, rooms]) => {
          return fhtChannels.length > 0 && rooms.length > 0;
        }),
        take(1)
      )
      .subscribe(([fhtChannels, rooms]) => {
        this.devicesLoading = false;
        this.roomsOptions = rooms.map((entry) => {
          return {
            value: entry.feature,
            viewValue: entry?.properties?.name?.value,
          };
        });
        this.channelsOptions = fhtChannels.map((entry) => {
          return {
            id: entry.id,
            value: null,
            viewValue: null,
            title: `${this.translateService.instant(
              '+SRC_COMMISSIONINGV2.FHT_CHANNEL'
            )} ${entry.id.slice(-1)}`,
          };
        });

        this.cdr.markForCheck();
      });
  }

  getAssignChannelDisplayState(): AssignChannelDisplayState {
    const roomsPresent = this.roomsOptions.length;
    const channelsPresent = this.channelsOptions.length;
    const loading = this.devicesLoading;

    if (loading) {
      return AssignChannelDisplayState.Loading;
    } else if (roomsPresent && channelsPresent) {
      return AssignChannelDisplayState.AssignRooms;
    } else if (!roomsPresent) {
      return AssignChannelDisplayState.ErrorNoRooms;
    } else if (!channelsPresent) {
      return AssignChannelDisplayState.ErrorNoChannels;
    } else {
      return AssignChannelDisplayState.Error;
    }
  }

  onSelect(event, channelOption): void {
    const viewValue = this.roomsOptions.find((entry) => {
      return entry.value === event;
    }).viewValue;

    channelOption.viewValue = viewValue;
  }

  navigateBack(): void {
    this.commissioningService.modalAction$.next(ModalAction.PreviousStep);
  }

  navigateForward(): void {
    const assignedChannels: CommissioningDeviceAssignChannelParams[] = this.channelsOptions
      .filter((entry) => {
        return entry.value !== null;
      })
      .map((entry) => {
        return {
          type: DeviceAssignmentType.Channel,
          fhtChannelDeviceId: entry.id,
          roomFeature: entry.value,
          roomName: entry.viewValue,
        };
      });

    this.commissioningStore.deviceAssignChannels(assignedChannels);

    this.commissioningService.modalAction$.next(ModalAction.NextStep);
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }
}
